import { nanoid } from "nanoid"
import type { FrontmatterReminder, SyncOperation } from "@caliu/types"
import { stripHighlights } from "./highlights"
import { MATH_BLOCK_REGEX, stripMath } from "./math"
import { stripAttachmentDownloadLinks } from "./attachments"
import { MARKDOWN_ESCAPE_RE } from "./noteTitle"

export { extractHashtags, stripCode, HASHTAG_REGEX, HASHTAG_TOKEN_CLASS, HASHTAG_BODY_PATTERN, normalizeHashtagBody, normalizeTagName, formatHashtag } from "./hashtags"
export {
  renameHashtagInContent,
  addHashtagToContent,
  removeHashtagFromContent,
} from "./tagContent"
export { buildTagHandle, buildTagHandles, type TagNode } from "./tagHandle"
export { tagPaletteIndex, TAG_PALETTE_SIZE } from "./tagPalette"
export { extractNoteTitle, hasHeadingTitle, hidesCardTitle, plainTextTitle, skipTitleFrontmatter, storedTitleText, unescapeMarkdown } from "./noteTitle"
export { legacyTiptapToMarkdown, legacyTiptapTitle, noteTitleForDisplay } from "./legacyTiptap"
export { MENTION_LABEL_MAX_LENGTH, truncateMentionLabel } from "./mentionLabel"
export { inviteRoles, memberRoles, type ShareLinkAccess, type ShareRole } from "./shareRoles"
export { noteEffectiveOrder, manualOrderBetween, createdAtToEpochMs, MANUAL_ORDER_GAP } from "./sortOrder"
export {
  expandBlankLines,
  blankParagraphCount,
  BLANK_LINE_SENTINEL,
  MAX_BLANK_PARAGRAPHS,
} from "./blankLines"
export {
  extractHighlights,
  stripHighlights,
  HIGHLIGHT_REGEX,
  HIGHLIGHT_COLORS,
  DEFAULT_HIGHLIGHT,
  type Highlight,
  type HighlightColor,
} from "./highlights"
export { MATH_INLINE_REGEX, MATH_BLOCK_REGEX, stripMath } from "./math"
export { resolveLocale, DEFAULT_LOCALE, SUPPORTED_LOCALES, LOCALE_META, type Locale, type LocaleMeta } from "./locale"
export {
  validateCatalogParity,
  formatParityIssues,
  validateNoForbiddenDashes,
  formatDashIssues,
  type ParityIssue,
  type DashIssue,
  type DashViolation,
} from "./i18nValidator"
export {
  resolveFontPreferences,
  normalizeFontPreferences,
  type ResolvedFonts,
  type CatalogEntry as FontResolverCatalogEntry,
} from "./fonts"
export {
  EDITOR_TYPOGRAPHY_RANGES,
  normalizeEditorTypography,
  snapTypographyValue,
  type TypographyRange,
} from "./editorTypography"
export {
  ATTACHMENT_URL_PATTERN,
  isAttachmentDownloadUrl,
  stripAttachmentDownloadLinks,
  attachmentIdFromUrl,
  formatFileSize,
  fileIconForName,
  type FileIconKey,
} from "./attachments"
export {
  threeWayMerge,
  assembleMerge,
  type MergeHunk,
  type MergeHunkType,
  type MergeResult,
  type MergeChoice,
} from "./merge"
export * from "./outline"
export { normalizeIconSearchText, searchIcons, type IconKeywordIndex } from "./iconSearch"
export {
  slugifyHeading,
  outlineWithSlugs,
  computeHeadingSections,
  sectionSiblings,
  foldedAncestors,
  type SluggedOutlineItem,
  type HeadingSection,
} from "./headingSections"
export { parseFrontmatterFolds, updateFrontmatterFolds } from "./foldFrontmatter"
export * from "./textOps"
export * from "./frontmatterMerge"
export * from "./noteContentSync"
export * from "./syncModeSwitch"
export * from "./captureMapping"
export * from "./undoStack"

/**
 * Generate a unique ID (nanoid wrapper).
 */
export function generateId(size?: number): string {
  return nanoid(size)
}

/**
 * Format a date string to ISO 8601.
 */
export function toISOString(date: Date | string | number): string {
  return new Date(date).toISOString()
}

/**
 * Return the current UTC timestamp as ISO 8601 string.
 */
export function now(): string {
  return new Date().toISOString()
}

export interface ParsedImageAlt {
  alt: string
  width?: number
}

/**
 * Parse Obsidian-style image width syntax from alt text: `alt|50%`.
 * Only a trailing 10-100 percent suffix on the last `|` is treated as width.
 */
export function parseImageAlt(rawAlt: string | null | undefined): ParsedImageAlt {
  const alt = rawAlt ?? ''
  const pipeIndex = alt.lastIndexOf('|')
  if (pipeIndex === -1) return { alt }

  const maybeWidth = alt.slice(pipeIndex + 1)
  const match = maybeWidth.match(/^(\d{1,3})%$/)
  if (!match) return { alt }

  const width = Number(match[1])
  if (!Number.isInteger(width) || width < 10 || width > 100) {
    return { alt }
  }

  return {
    alt: alt.slice(0, pipeIndex),
    width,
  }
}

/**
 * Format alt text with an optional Obsidian-style width suffix: `alt|50%`.
 */
export function formatImageAlt(
  alt: string | null | undefined,
  width: number | null | undefined,
): string {
  const normalizedAlt = alt ?? ''
  if (!Number.isInteger(width) || width == null || width < 10 || width > 100) {
    return normalizedAlt
  }
  return `${normalizedAlt}|${width}%`
}

// ── Sync Deduplication ─────────────────────────────────────────────────────
// Pure function: no I/O, no platform deps. Can be unit tested independently
// and serves as the reference implementation for Swift/Kotlin ports.

/**
 * Fold the payload of a LATER queued op into an EARLIER one for the same
 * entity. Shallow merge, the later op winning every key collision, with one
 * exception: `expected_updated_at` keeps the EARLIER op's value when it has
 * one. That value is the last `updated_at` this device saw from the server
 * before the first of the merged edits; the later op's value is only the
 * timestamp the earlier LOCAL edit wrote, which the server has never seen, so
 * sending it would hide a foreign write that landed in between.
 */
function mergeSyncOpData(earlier: unknown, later: unknown): unknown {
  if (later == null) return earlier
  if (earlier == null) return later
  const base = earlier as Record<string, unknown>
  const merged = { ...base, ...(later as Record<string, unknown>) }
  if (base.expected_updated_at != null) merged.expected_updated_at = base.expected_updated_at
  return merged
}

/**
 * Deduplicate a list of sync operations for the same (entity, entityId) key.
 *
 * `ops` MUST be in queue order (the queue's row id, ascending). That order is
 * the only notion of "earlier" and "later" here: the ops' wall-clock
 * `timestamp` never decides anything, because a clock that steps back between
 * two edits would otherwise keep the OLDER text.
 *
 * Rules, applied to each op against the current winner for its key:
 *  1. No winner yet: the op becomes the winner.
 *  2. An `update` after a `create` or an `update` is MERGED into it (see
 *     `mergeSyncOpData`). A create is never discarded in favour of an update
 *     (the server has not seen the entity yet), and neither side of two
 *     updates is dropped: each update op carries only its own changed fields
 *     (content edit: `content`; pin/archive/sort_order/read-only: that one
 *     flag), and the server applies each field independently, so the combined
 *     op is valid. The survivor is the create, or the later update.
 *  3. A `restore` after a `soft-delete` replaces it, and a `create` after a
 *     `delete` replaces it: between an op and the later op that undoes it,
 *     order decides. Priority used to (soft-delete 3 beat restore 2, delete 4
 *     beat create 2), which pushed the trash and dropped the restore, leaving
 *     the note in the server's trash for good, and pushed a `note_tag` unlink
 *     while the device kept showing the link.
 *  4. Otherwise priority decides: delete (4) > soft-delete (3) >
 *     create/restore (2) > update (1). The op replaces the winner when its
 *     priority is higher or equal, and is dropped when it is lower.
 *
 * Returns: { latest: Map of deduplicated ops, supersededIds: IDs to remove }
 *
 * Mirrored in Swift (`DeduplicateSyncOps.swift`) and Kotlin
 * (`DeduplicateSyncOps.kt`); the three run `syncDedup.fixtures.json`. Change
 * all of them or none.
 */
export function deduplicateSyncOps(ops: SyncOperation[]): {
  latest: Map<string, SyncOperation>
  supersededIds: number[]
} {
  const latestByKey = new Map<string, SyncOperation>()
  const typePriority: Record<string, number> = { delete: 4, 'soft-delete': 3, create: 2, restore: 2, update: 1 }

  for (const op of ops) {
    const key = `${op.entity}:${op.entityId}`
    const existing = latestByKey.get(key)
    if (!existing) {
      latestByKey.set(key, op)
      continue
    }

    if (op.type === 'update' && (existing.type === 'update' || existing.type === 'create')) {
      const survivor = existing.type === 'create' ? existing : op
      latestByKey.set(key, {
        ...survivor,
        data: mergeSyncOpData(existing.data, op.data),
        timestamp: Math.max(existing.timestamp, op.timestamp),
      })
      continue
    }

    const undoesExisting =
      (existing.type === 'soft-delete' && op.type === 'restore') ||
      (existing.type === 'delete' && op.type === 'create')
    if (undoesExisting || (typePriority[op.type] ?? 0) >= (typePriority[existing.type] ?? 0)) {
      latestByKey.set(key, op)
    }
  }

  const latestIds = new Set(
    Array.from(latestByKey.values()).map((o) => o.id!)
  )
  const supersededIds = ops
    .filter((o) => !latestIds.has(o.id!))
    .map((o) => o.id!)

  return { latest: latestByKey, supersededIds }
}

/**
 * Normalize tags that may be {id,name}[] objects or string[] IDs to string[].
 * Shared between sync engine, hooks, and conflict resolution.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function normalizeTagIds(tags: any[]): string[] {
  return tags.map((t) => (typeof t === 'object' && t?.id ? t.id : t) as string)
}

// ── Note Preview ───────────────────────────────────────────────────────────
// Reference implementation shared by web (note rows) and worker (push
// notification bodies). Mirrors the Swift `previewMarkdownLines` /
// `dropTitleLine` pipeline in `MarkdownStripper.swift` so all three clients
// produce the same preview text from the same content.

const PREVIEW_FENCE_RE = /^(?:`{3,}|~{3,}).*$/
const PREVIEW_THEMATIC_BREAK_RE = /^[-*_]{3,}$/
// A bare marker (`#`, what an empty heading trims to) is a prefix too, so it
// is never readable preview text. Swift and Kotlin match it the same way.
const PREVIEW_HEADING_PREFIX_RE = /^#{1,6}(?:\s+|$)/
const PREVIEW_BLOCKQUOTE_PREFIX_RE = /^\s*(?:>\s*)+/
const PREVIEW_TASK_LIST_PREFIX_RE = /^\s*[-*+]\s+\[( |x|X)\]\s+/
const PREVIEW_UNORDERED_LIST_PREFIX_RE = /^\s*[-*+]\s+/
const PREVIEW_ORDERED_LIST_PREFIX_RE = /^\s*\d+[.)]\s+/
const PREVIEW_IMAGE_RE = /!\[[^\]]*\]\([^)]*\)/g
const PREVIEW_INLINE_CODE_RE = /`([^`]+)`/g
const PREVIEW_BOLD_RE = /\*\*([^*]+)\*\*/g
const PREVIEW_BOLD_ALT_RE = /__([^_]+)__/g
const PREVIEW_ITALIC_RE = /\*([^*]+)\*/g
const PREVIEW_ITALIC_ALT_RE = /_([^_]+)_/g
const PREVIEW_STRIKETHROUGH_RE = /~~([^~]+)~~/g
const PREVIEW_LINK_RE = /\[([^\]]+)\]\([^)]+\)/g

function isFenceLine(line: string): boolean {
  return PREVIEW_FENCE_RE.test(line)
}

function isDecoratorLine(line: string): boolean {
  if (!line) return false
  return PREVIEW_THEMATIC_BREAK_RE.test(line) || PREVIEW_FENCE_RE.test(line)
}

function isPotentialTableRow(line: string): boolean {
  return line.includes('|')
}

function isMarkdownTableSeparator(line: string): boolean {
  if (!line.includes('|')) return false
  const trimmed = line.replace(/^\||\|$/g, '').trim()
  if (!trimmed) return false
  const cells = trimmed.split('|')
  if (cells.length === 0) return false
  for (const cell of cells) {
    const token = cell.trim()
    if (!token) return false
    const dashes = token.replace(/[:-]/g, '')
    if (dashes) return false
    const dashCount = (token.match(/-/g) || []).length
    if (dashCount < 3) return false
  }
  return true
}

function startsMarkdownTable(lines: string[], index: number): boolean {
  if (index + 1 >= lines.length) return false
  const header = lines[index].trim()
  const separator = lines[index + 1].trim()
  return isPotentialTableRow(header) && isMarkdownTableSeparator(separator)
}

function indexAfterMarkdownTable(lines: string[], index: number): number {
  let next = index + 2
  while (next < lines.length) {
    const trimmed = lines[next].trim()
    if (!trimmed || !isPotentialTableRow(trimmed)) break
    next += 1
  }
  return next
}

/**
 * Expand markdown table blocks into one line per cell, in reading order, so
 * title derivation treats each cell as its own line (first readable cell →
 * title). In-cell `<br>` breaks split into separate lines too; the divider row
 * and empty cells are dropped. Non-table lines pass through. Used only by
 * title extraction (autosave) — NOT by the preview pipeline, which drops whole
 * tables so cell text never leaks into the preview. Mirrors Swift's
 * `explodeTableCells`.
 */
export function explodeTableCells(text: string): string {
  if (!text) return text
  const lines = text.split('\n')
  const result: string[] = []
  let index = 0
  while (index < lines.length) {
    if (startsMarkdownTable(lines, index)) {
      const end = indexAfterMarkdownTable(lines, index)
      for (let row = index; row < end; row += 1) {
        const trimmed = lines[row].trim()
        if (isMarkdownTableSeparator(trimmed)) continue
        // Split on unescaped `|` only — a `\|` (e.g. the image width syntax
        // `![a\|100%]`) is a literal pipe inside the cell, not a separator.
        // Then unescape it so the cell text reads naturally.
        for (const cell of trimmed.split(/(?<!\\)\|/)) {
          const unescaped = cell.replace(/\\\|/g, '|')
          for (const piece of unescaped.split(/<br\s*\/?>/gi)) {
            const value = piece.trim()
            if (value) result.push(value)
          }
        }
      }
      index = end
    } else {
      result.push(lines[index])
      index += 1
    }
  }
  return result.join('\n')
}

function cleanedPreviewLine(line: string, listMarkers = false): string | null {
  let result = line
  // `<br>` (table cells serialize in-cell newlines to it) → space so previews
  // never show the literal tag. Mirrors Swift's `htmlLineBreakPattern`.
  result = result.replace(/<br\s*\/?>/gi, ' ')
  result = result.replace(PREVIEW_HEADING_PREFIX_RE, '')
  result = result.replace(PREVIEW_BLOCKQUOTE_PREFIX_RE, '')
  const taskMatch = result.match(PREVIEW_TASK_LIST_PREFIX_RE)
  if (taskMatch) {
    const marker = taskMatch[1].toLowerCase() === 'x' ? '◉ ' : '○ '
    result = marker + result.slice(taskMatch[0].length)
  } else if (listMarkers) {
    // Swift's preview items: a bullet reads "• item", a number stays (it is
    // the marker, and "20. Oktober" is a date as much as a list).
    result = result.replace(PREVIEW_UNORDERED_LIST_PREFIX_RE, '• ')
  } else {
    result = result.replace(PREVIEW_UNORDERED_LIST_PREFIX_RE, '')
    result = result.replace(PREVIEW_ORDERED_LIST_PREFIX_RE, '')
  }
  result = result.replace(PREVIEW_IMAGE_RE, '')
  // Drop file-attachment links entirely (they render as chips, not text).
  result = stripAttachmentDownloadLinks(result)
  result = result.trim()
  return result || null
}

/**
 * Turn one cleaned preview line into plain text. Only runs on the
 * `stripInline: true` path — the caller that leaves inline markdown in place
 * hands the line to a markdown renderer (Swift's `renderInlineMarkdown`), which
 * does its own unescaping, so unescaping here would make `\*x\*` render as real
 * emphasis. Mirrors Swift's `stripMarkdown` in `MarkdownStripper.swift`.
 */
function stripInlineFormatting(line: string): string {
  return (
    stripMath(stripHighlights(line))
      .replace(PREVIEW_INLINE_CODE_RE, '$1')
      .replace(PREVIEW_BOLD_RE, '$1')
      .replace(PREVIEW_BOLD_ALT_RE, '$1')
      .replace(PREVIEW_STRIKETHROUGH_RE, '$1')
      .replace(PREVIEW_LINK_RE, '$1')
      .replace(PREVIEW_ITALIC_RE, '$1')
      .replace(PREVIEW_ITALIC_ALT_RE, '$1')
      // LAST, exactly as in `plainTextTitle`. The block-prefix strips already
      // ran in `cleanedPreviewLine`, so unescaping any earlier would let
      // PREVIEW_ORDERED_LIST_PREFIX_RE eat the number of a `1\. …` line (and
      // the heading / bullet / quote rules eat their markers too).
      .replace(MARKDOWN_ESCAPE_RE, '$1')
  )
}

/**
 * True when any cell of the table block `[start, end)` yields readable title
 * text (i.e. is not image-only, attachment-only, or empty). Mirrors the skip
 * rule used by title derivation, so `dropTitleLine` can tell whether a leading
 * table sources the title.
 */
function tableHasReadableTitleCell(lines: string[], start: number, end: number): boolean {
  const block = lines.slice(start, end).join('\n')
  for (const cell of explodeTableCells(block).split('\n')) {
    if (cleanedPreviewLine(cell.trim())) return true
  }
  return false
}

/**
 * Drop the first non-blank, non-decorator line from `text` — assumed to be
 * the note's title. A leading table whose cells yield title text is the title
 * source, so the whole block is dropped; a media/empty table sources no title,
 * so it is skipped and the line after it is dropped instead. Mirrors Swift's
 * `dropTitleLine`.
 */
export function dropTitleLine(text: string): string {
  if (!text) return text
  const lines = text.split('\n')
  let inFence = false
  let titleIndex = -1
  for (let i = 0; i < lines.length; i += 1) {
    const trimmed = lines[i].trim()
    if (inFence) {
      if (isFenceLine(trimmed)) inFence = false
      continue
    }
    if (isFenceLine(trimmed)) {
      inFence = true
      continue
    }
    if (!trimmed || isDecoratorLine(trimmed)) continue
    if (startsMarkdownTable(lines, i)) {
      const end = indexAfterMarkdownTable(lines, i)
      // When a table cell yields readable text, that cell is the title — drop
      // the whole block so its other cells never leak into the preview.
      if (tableHasReadableTitleCell(lines, i, end)) {
        return lines.slice(end).join('\n')
      }
      // All-media / empty table: it sources no title, so the title is a line
      // after it. Skip past the table (preview drops it anyway) and keep
      // scanning so the real title line is the one dropped — not duplicated.
      i = end - 1
      continue
    }
    // Image-only and attachment-only lines render as chips, never as the
    // title (title extraction skips them too). Skip them here so we drop the
    // line that actually became the title. Otherwise a leading
    // `[file.pdf](…/download)` is dropped instead and the real title heading
    // leaks into the preview, duplicating the title. (mirror: Swift dropTitleLine)
    if (!cleanedPreviewLine(trimmed)) continue
    titleIndex = i
    break
  }
  if (titleIndex === -1) return text
  return lines.slice(titleIndex + 1).join('\n')
}

export interface NotePreviewOptions {
  /** Maximum number of cleaned lines to include. Defaults to 6. */
  lineLimit?: number
  /** Whether to strip inline markdown (bold/italic/links). Defaults to false
   * so callers like Swift's `renderInlineMarkdown` can render attributes. */
  stripInline?: boolean
  /** Keep the title line instead of dropping it. For a note without a clear
   * title (`hasHeadingTitle` false), whose card shows no title row. */
  keepTitle?: boolean
  /** Keep list markers as the native rows show them: `• ` for a bullet, the
   * number of an ordered item (Swift `notePreviewLineItems`). For cards, where
   * line 1 can stand in for the title. */
  listMarkers?: boolean
}

/**
 * Cleaned preview lines from raw note content. Mirrors Swift's
 * `previewMarkdownLines` — preserves heading text (markers stripped),
 * paragraphs, blockquote text, list items (task items shown as ◉/○);
 * skips fenced code, tables, and image-only lines. Frontmatter and the
 * first title line (unless `keepTitle`) are stripped before processing.
 */
export function notePreviewLines(content: string, options: NotePreviewOptions = {}): string[] {
  const lineLimit = options.lineLimit ?? 6
  if (lineLimit <= 0 || !content) return []
  const { body } = splitFrontmatterDocument(content)
  // Do NOT explode table cells here — tables are dropped from previews whole
  // (dropTitleLine drops a leading one, the loop below skips the rest) so cell
  // text never leaks into the preview. Title derivation still explodes (above).
  const withoutTitle = options.keepTitle ? body : dropTitleLine(body)
  if (!withoutTitle) return []

  // Display math spans lines, so it has to go before the per-line loop —
  // `stripInlineFormatting` only ever sees one line and would leave the
  // `$$` fences and raw LaTeX in the preview.
  const lines = withoutTitle.replace(MATH_BLOCK_REGEX, '').split('\n')
  const results: string[] = []
  let index = 0
  let inFence = false

  while (index < lines.length && results.length < lineLimit) {
    const trimmed = lines[index].trim()

    if (inFence) {
      if (isFenceLine(trimmed)) inFence = false
      index += 1
      continue
    }
    if (isFenceLine(trimmed)) {
      inFence = true
      index += 1
      continue
    }
    if (!trimmed || isDecoratorLine(trimmed)) {
      index += 1
      continue
    }
    if (startsMarkdownTable(lines, index)) {
      index = indexAfterMarkdownTable(lines, index)
      continue
    }
    if (isMarkdownTableSeparator(trimmed)) {
      index += 1
      continue
    }

    const cleaned = cleanedPreviewLine(trimmed, options.listMarkers)
    if (cleaned) {
      results.push(options.stripInline ? stripInlineFormatting(cleaned) : cleaned)
    }
    index += 1
  }

  return results
}

/**
 * Plain-text preview joined with newlines. Used for push notification
 * bodies (worker) and any plain-text rendering (web list rows, command
 * palette). Inline markdown is always stripped.
 */
export function notePreviewText(content: string, options: NotePreviewOptions = {}): string {
  return notePreviewLines(content, { ...options, stripInline: true }).join('\n')
}

// ── Frontmatter Reminder Projection ────────────────────────────────────────

export interface FrontmatterDocument {
  frontmatter: string | null
  body: string
}

const FRONTMATTER_BOUNDARY = '---'
const REMINDER_FIELD_ORDER = [
  'id',
  'title',
  'triggerAt',
  'recurrence',
  'snoozedUntil',
  'firedAt',
  'dismissedAt',
  'createdAt',
  'updatedAt',
] as const

export function splitFrontmatterDocument(content: string): FrontmatterDocument {
  if (!content.startsWith(`${FRONTMATTER_BOUNDARY}\n`)) {
    return { frontmatter: null, body: content }
  }

  const endIdx = content.indexOf(`\n${FRONTMATTER_BOUNDARY}\n`, FRONTMATTER_BOUNDARY.length + 1)
  if (endIdx === -1) {
    return { frontmatter: null, body: content }
  }

  const frontmatter = content.slice(FRONTMATTER_BOUNDARY.length + 1, endIdx)
  const body = content.slice(endIdx + `\n${FRONTMATTER_BOUNDARY}\n`.length)
  return { frontmatter, body }
}

function normalizeScalar(raw: string): string | null {
  const trimmed = raw.trim()
  if (!trimmed || trimmed === 'null') return null
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    return trimmed.slice(1, -1)
  }
  return trimmed
}

function parseReminderItem(lines: string[]): FrontmatterReminder | null {
  const values = new Map<string, string | null>()

  for (const line of lines) {
    const match = line.match(/^\s{4}([A-Za-z0-9_-]+):\s*(.*)$/)
    if (!match) continue
    const [, key, raw] = match
    values.set(key, normalizeScalar(raw))
  }

  const id = values.get('id')
  const triggerAt = values.get('triggerAt') ?? values.get('trigger_at')
  if (!id || !triggerAt) return null

  return {
    id,
    triggerAt,
    recurrence: values.get('recurrence') ?? null,
    snoozedUntil: values.get('snoozedUntil') ?? values.get('snoozed_until') ?? null,
    firedAt: values.get('firedAt') ?? values.get('fired_at') ?? null,
    dismissedAt: values.get('dismissedAt') ?? values.get('dismissed_at') ?? null,
    createdAt: values.get('createdAt') ?? values.get('created_at') ?? null,
    updatedAt: values.get('updatedAt') ?? values.get('updated_at') ?? null,
    title: values.get('title') ?? null,
  }
}

export function parseFrontmatterReminders(content: string): FrontmatterReminder[] {
  const { frontmatter } = splitFrontmatterDocument(content)
  if (!frontmatter) return []

  const lines = frontmatter.split('\n')
  const reminders: FrontmatterReminder[] = []
  let inReminders = false
  let currentItem: string[] = []

  const flushCurrent = () => {
    if (currentItem.length === 0) return
    const parsed = parseReminderItem(currentItem)
    if (parsed) reminders.push(parsed)
    currentItem = []
  }

  for (const line of lines) {
    if (!inReminders) {
      if (/^reminders:\s*$/.test(line)) {
        inReminders = true
      }
      continue
    }

    if (/^[A-Za-z0-9_-]+:\s*/.test(line)) {
      flushCurrent()
      break
    }

    if (/^\s{2}-\s*/.test(line)) {
      flushCurrent()
      const inlineMatch = line.match(/^\s{2}-\s*([A-Za-z0-9_-]+):\s*(.*)$/)
      if (inlineMatch) {
        const [, key, raw] = inlineMatch
        currentItem.push(`    ${key}: ${raw}`)
      }
      continue
    }

    if (/^\s{4}[A-Za-z0-9_-]+:\s*/.test(line)) {
      currentItem.push(line)
      continue
    }

    if (!line.trim()) continue
    flushCurrent()
    break
  }

  flushCurrent()
  return reminders
}

function escapeScalar(value: string): string {
  return JSON.stringify(value)
}

function serializeReminderItem(reminder: FrontmatterReminder): string[] {
  const normalized: Record<string, string | null | undefined> = {
    id: reminder.id,
    title: reminder.title ?? null,
    triggerAt: reminder.triggerAt,
    recurrence: reminder.recurrence ?? null,
    snoozedUntil: reminder.snoozedUntil ?? null,
    firedAt: reminder.firedAt ?? null,
    dismissedAt: reminder.dismissedAt ?? null,
    createdAt: reminder.createdAt ?? null,
    updatedAt: reminder.updatedAt ?? null,
  }

  const lines: string[] = []
  let wroteFirst = false
  for (const key of REMINDER_FIELD_ORDER) {
    const value = normalized[key]
    if (value === undefined) continue
    const rendered = value === null ? 'null' : escapeScalar(value)
    if (!wroteFirst) {
      lines.push(`  - ${key}: ${rendered}`)
      wroteFirst = true
    } else {
      lines.push(`    ${key}: ${rendered}`)
    }
  }
  return lines
}

function replaceReminderSection(frontmatter: string, serializedSection: string | null): string {
  const lines = frontmatter ? frontmatter.split('\n') : []
  const out: string[] = []

  let i = 0
  let replaced = false
  while (i < lines.length) {
    const line = lines[i]
    if (!/^reminders:\s*$/.test(line)) {
      out.push(line)
      i += 1
      continue
    }

    replaced = true
    if (serializedSection) out.push(...serializedSection.split('\n'))
    i += 1
    while (i < lines.length) {
      const next = lines[i]
      if (/^[A-Za-z0-9_-]+:\s*/.test(next)) break
      i += 1
    }
  }

  if (!replaced && serializedSection) {
    if (out.length > 0 && out[out.length - 1] !== '') out.push('')
    out.push(...serializedSection.split('\n'))
  }

  return out.join('\n').replace(/\n{3,}/g, '\n\n').replace(/^\n+|\n+$/g, '')
}

export function updateFrontmatterReminders(content: string, reminders: FrontmatterReminder[]): string {
  const { frontmatter, body } = splitFrontmatterDocument(content)
  const serializedSection = reminders.length > 0
    ? ['reminders:', ...reminders.flatMap(serializeReminderItem)].join('\n')
    : null

  const nextFrontmatter = replaceReminderSection(frontmatter ?? '', serializedSection)

  if (!nextFrontmatter) return body
  return `${FRONTMATTER_BOUNDARY}\n${nextFrontmatter}\n${FRONTMATTER_BOUNDARY}\n${body}`
}

export function upsertFrontmatterReminder(content: string, reminder: FrontmatterReminder): string {
  const reminders = parseFrontmatterReminders(content)
  const idx = reminders.findIndex((item) => item.id === reminder.id)
  if (idx === -1) reminders.push(reminder)
  else reminders[idx] = reminder
  return updateFrontmatterReminders(content, reminders)
}

export function removeFrontmatterReminder(content: string, reminderId: string): string {
  const reminders = parseFrontmatterReminders(content).filter((item) => item.id !== reminderId)
  return updateFrontmatterReminders(content, reminders)
}
export { parseSearchQuery, matchRangesForTerms, searchSnippet, type ParsedQuery, type QueryGroup, type SearchTerm, type TextTerm, type TagTerm, type SpecialTerm, type SpecialName, type DateSpec } from "./searchQuery"
